import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Compass, Download, RotateCcw } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import {
  DESIGNER_ROUTINE_SECTIONS,
  DESIGNER_ROUTINE_STATUS_LABELS,
  type DesignerRoutineStatus,
} from "@/lib/basProcess/designerRoutine";
import { generateDesignerRoutineWordDocument } from "@/lib/documentGenerator/designerRoutineWordGenerator";

interface Props {
  projectId: string;
}

interface Routine {
  id: string;
  name: string;
  version: string;
  status: string;
  sections: Record<string, string>;
}

const defaultSections = () =>
  Object.fromEntries(DESIGNER_ROUTINE_SECTIONS.map((s) => [s.key, s.template])) as Record<
    string,
    string
  >;

export const DesignerRoutine: React.FC<Props> = ({ projectId }) => {
  const [routine, setRoutine] = useState<Routine | null>(null);
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data, error }, { data: proj }] = await Promise.all([
      supabase
        .from("designer_routines")
        .select("id, name, version, status, sections")
        .eq("project_id", projectId)
        .order("created_at", { ascending: true })
        .maybeSingle(),
      supabase.from("projects").select("name").eq("id", projectId).maybeSingle(),
    ]);
    if (error) toast.error("Kunde inte läsa rutinen");
    setProjectName(proj?.name ?? "");
    setRoutine(data ? ({ ...data, sections: (data.sections as any) || {} } as Routine) : null);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const create = async () => {
    setBusy(true);
    const { data, error } = await supabase
      .from("designer_routines")
      .insert({ project_id: projectId, sections: defaultSections() })
      .select("id, name, version, status, sections")
      .single();
    setBusy(false);
    if (error || !data) return toast.error("Kunde inte skapa rutinen");
    setRoutine({ ...data, sections: (data.sections as any) || {} } as Routine);
    toast.success("Rutinen skapad med förifylld standardtext");
  };

  const update = async (patch: Partial<Routine>) => {
    if (!routine) return;
    setRoutine({ ...routine, ...patch });
    const { error } = await supabase
      .from("designer_routines")
      .update(patch as any)
      .eq("id", routine.id);
    if (error) toast.error("Kunde inte spara");
  };

  const updateSection = (key: string, value: string) => {
    if (!routine) return;
    update({ sections: { ...routine.sections, [key]: value } });
  };

  const resetSection = (key: string) => {
    updateSection(key, DESIGNER_ROUTINE_SECTIONS.find((s) => s.key === key)?.template ?? "");
    toast.success("Standardtexten återställd");
  };

  const exportWord = async () => {
    if (!routine) return;
    try {
      await generateDesignerRoutineWordDocument(routine, projectName);
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  const filled = routine
    ? DESIGNER_ROUTINE_SECTIONS.filter((s) => (routine.sections[s.key] || "").trim().length > 30)
        .length
    : 0;
  const progress = Math.round((filled / DESIGNER_ROUTINE_SECTIONS.length) * 100);

  if (loading)
    return <div className="py-12 text-center text-muted-foreground">Laddar rutinen...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="18"
        exportHistory={{ documentType: "projektorrutin" }}
        title="Projektörens arbetsmiljörutin"
        description="Beskriver hur projektören identifierar, bygger bort och redovisar arbetsmiljörisker. Kan lämnas till ett projekt som inte använder systemet, som bevis på hur ansvaret uppfylls."
        legalRef="AML 3 kap. 7 a § · AFS 2023:3 kap. 2"
        icon={Compass}
        meta={
          routine ? (
            <div className="flex items-center gap-4">
              <Badge variant={routine.status === "faststalld" ? "default" : "outline"}>
                {DESIGNER_ROUTINE_STATUS_LABELS[routine.status as DesignerRoutineStatus] ||
                  routine.status}
              </Badge>
              <div className="w-40">
                <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                  <span>Ifyllda avsnitt</span>
                  <span>
                    {filled}/{DESIGNER_ROUTINE_SECTIONS.length}
                  </span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            </div>
          ) : null
        }
        actions={
          routine ? (
            <Button size="sm" variant="outline" onClick={exportWord}>
              <Download className="mr-1 h-4 w-4" /> Word
            </Button>
          ) : null
        }
      />

      {!routine ? (
        <Card>
          <CardContent className="space-y-3 py-10 text-center">
            <Compass className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Ingen rutin upprättad ännu. Skapa den med förifylld standardtext och justera efter
              uppdraget.
            </p>
            <Button onClick={create} disabled={busy}>
              {busy ? "Skapar..." : "Skapa rutin"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="grid gap-2 pt-6 sm:grid-cols-[1fr_120px_320px]">
              <Input
                key={`n-${routine.id}`}
                defaultValue={routine.name}
                placeholder="Dokumentnamn"
                onBlur={(e) => update({ name: e.target.value })}
              />
              <Input
                key={`v-${routine.id}`}
                defaultValue={routine.version}
                placeholder="Version"
                onBlur={(e) => update({ version: e.target.value })}
              />
              <div className="flex gap-2">
                {(Object.keys(DESIGNER_ROUTINE_STATUS_LABELS) as DesignerRoutineStatus[]).map(
                  (s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant={routine.status === s ? "default" : "outline"}
                      onClick={() => update({ status: s })}
                    >
                      {DESIGNER_ROUTINE_STATUS_LABELS[s]}
                    </Button>
                  )
                )}
              </div>
            </CardContent>
          </Card>

          {DESIGNER_ROUTINE_SECTIONS.map((s) => (
            <Card key={s.key}>
              <CardContent className="space-y-2 pt-6">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h2 className="text-sm font-semibold">{s.title}</h2>
                    <p className="text-xs text-muted-foreground">{s.hint}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => resetSection(s.key)}>
                    <RotateCcw className="mr-1 h-4 w-4" /> Återställ standardtext
                  </Button>
                </div>
                <Textarea
                  key={`${routine.id}-${s.key}`}
                  rows={6}
                  defaultValue={routine.sections[s.key] ?? ""}
                  placeholder={s.template}
                  onBlur={(e) => {
                    if (e.target.value !== (routine.sections[s.key] ?? "")) {
                      updateSection(s.key, e.target.value);
                    }
                  }}
                />
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
};

export default DesignerRoutine;
